import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VaultItem } from 'src/app/models/vault-item';
import { VaultService } from 'src/app/services/vault.service';
import { FormControl, FormGroup } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs';
import { DialogService } from '../../../services/dialog.service';

@Component({
    selector: 'app-vault-detail',
    templateUrl: './vault-detail.component.html',
    styleUrls: ['./vault-detail.component.scss'],
    standalone: false
})
export class VaultDetailComponent implements OnInit {
  private dialogs = inject(DialogService);
  public isLoading: boolean = true;

  @Input() isWidget: boolean = false;
  @Input() teamName: string;
  @Input() vaultName: string;
  @Input() parentId: string;
  @Output() onCloseDetail = new EventEmitter<void>();

  public data: VaultItem = new VaultItem();
  public isVaulted: boolean = false;
  public errorMessage: string;
  public updatingVault: boolean = false;
  public originalName: string;
  public saveStatus: 'COMPLETE' | 'ERROR' | 'SAVING' = 'COMPLETE';

  public vaultForm = new FormGroup({
    name: new FormControl<any>(null),
    value: new FormControl<any>(null),
    color: new FormControl<any>(null)
  });

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private vaultService: VaultService
  ) {}

  ngOnInit(): void {
    this.vaultForm.controls['name'].disable();
    this.vaultForm.controls['value'].disable();
    this.vaultForm.controls['color'].disable();

    if (this.isWidget) {
      this.load(this.teamName, this.vaultName);
    } else {
      this.activatedRoute.params.subscribe((params) => {
        this.load(params.team, params.vaultName);
      });
    }
  }

  subscribeToFormChanges() {
    this.vaultForm.setValue({
      name: this.data.name,
      value: this.data.value,
      color: this.data.color || null
    });

    this.vaultForm.valueChanges.pipe(distinctUntilChanged()).subscribe((value: any) => {
      const raw = this.vaultForm.getRawValue();
      this.data.name = raw.name;
      this.data.value = raw.value?.replace(/,/g, '');
      this.data.color = raw.color || null;
    });
  }

  async load(teamName: string, vaultName: string) {
    this.teamName = teamName;

    const sub = this.vaultService.get(teamName, vaultName).subscribe(async (response: any) => {
      sub.unsubscribe();

      if (response.vaultItem && response.vaultItem.model) {
        this.isVaulted = true;
        this.data = response.vaultItem;
        this.originalName = this.data.name;

        this.vaultForm.controls['value'].enable();
        this.vaultForm.controls['color'].enable();

        if (this.data.workers.length == 0) this.vaultForm.controls['name'].enable();
      }

      this.subscribeToFormChanges();
      this.isLoading = false;
    });
  }

  async removeFromVault() {
    if (!await this.dialogs.confirm(`Are you sure you want to delete ${this.data.name}?`)) return;

    this.updatingVault = true;
    const sub = this.vaultService.remove(this.teamName, this.data.name).subscribe((response) => {
      sub.unsubscribe();
      this.isVaulted = false;
      this.data = new VaultItem();
      this.updatingVault = false;

      this.vaultForm.controls['name'].disable();
      this.vaultForm.controls['value'].disable();
      this.vaultForm.controls['color'].disable();

      if (this.isWidget) {
        this.onCloseDetail.emit();
      } else {
        this.router.navigate([this.teamName, 'vault']);
      }
    });
  }

  async save() {
    this.saveStatus = 'SAVING';

    this.updatingVault = true;
    this.errorMessage = null;
    let reloadAfterUpdate = false;

    let new_name = null;
    if (this.data.name != this.originalName) {
      new_name = this.data.name;
      this.data.new_name = new_name;
      this.data.name = this.originalName;
      reloadAfterUpdate = true;
    }

    const sub = this.vaultService.update(this.teamName, this.data).subscribe(async (response) => {
      sub.unsubscribe();
      if (response.error) {
        this.saveStatus = 'ERROR';
        this.errorMessage = response.error;
      }

      this.updatingVault = false;
      this.saveStatus = 'COMPLETE';

      if (reloadAfterUpdate) {
        this.isLoading = true;
        this.data = new VaultItem();

        if (this.isWidget) {
          this.vaultName = new_name;
          this.load(this.teamName, this.vaultName);
        } else {
          this.router.navigate([this.teamName, 'vault-detail', new_name]);
        }
      }
    });
  }
}
